import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Link2, Loader2, Check, RefreshCw, Unlink, AlertTriangle } from 'lucide-react';

/**
 * Instagram Login connect — Sprint 61 (1/N). Own product from Meta
 * ("Instagram API with Instagram Login"), own OAuth dialog, own tenant
 * connection. Simpler than MetaFanPageConnect.js: no page-picker step,
 * since the token already authenticates exactly one Instagram account.
 * @param {object} props
 * @param {function} [props.onConnected]
 * @param {boolean} [props.compact]
 */
export default function InstagramLoginConnect({ onConnected, compact = false }) {
  const [status, setStatus] = useState('loading'); // loading | not_connected | connected | error
  const [incompleteAuth, setIncompleteAuth] = useState(false);
  const [username, setUsername] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [togglingAi, setTogglingAi] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [webhookSubscribed, setWebhookSubscribed] = useState(null);
  const [longLivedToken, setLongLivedToken] = useState(true);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [reconnectRequired, setReconnectRequired] = useState(false);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const { data } = await api.get('/tenant/oauth/instagram/account');
      if (data.connected) {
        setUsername(data.username);
        setAiEnabled(data.ai_enabled !== false);
        setWebhookSubscribed(data.webhook_subscribed);
        setLongLivedToken(data.long_lived_token !== false);
        setTokenExpired(!!data.token_expired);
        setReconnectRequired(!!data.reconnect_required);
        setStatus('connected');
      } else {
        setStatus('not_connected');
      }
    } catch (e) {
      setStatus('error');
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Vuelve del redirect de Instagram con ?oauth=instagram&connected=1, o
  // con &connected=0&reason=instagram_incomplete_auth cuando el backend
  // rechazó la conexión en fail-closed (canje a token largo fallido, o
  // /me nunca identificó una cuenta profesional real) — en ese caso no
  // hay nada conectado que recargar, pero sí queremos avisar y dejar el
  // botón de reintento a la vista, no solo la pantalla genérica de
  // "conecta tu cuenta" como si nunca se hubiera intentado.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') === 'instagram') {
      const failedIncomplete = params.get('connected') === '0' && params.get('reason') === 'instagram_incomplete_auth';
      if (failedIncomplete) {
        setIncompleteAuth(true);
        toast.error('Autorización de Instagram incompleta — vuelve a intentarlo o verifica la cuenta que autorizaste.');
        setStatus('not_connected');
      } else {
        load().then(() => onConnected?.());
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth'); url.searchParams.delete('connected'); url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setIncompleteAuth(false);
    setRedirecting(true);
    try {
      const { data } = await api.get('/tenant/oauth/instagram/authorize', {
        params: { return_to: window.location.pathname },
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo iniciar la conexión con Instagram');
      setRedirecting(false);
      setReconnecting(false);
    }
  };

  const disconnect = async ({ confirm = true } = {}) => {
    if (confirm && !window.confirm('¿Desconectar tu cuenta de Instagram?')) return false;
    setDisconnecting(true);
    try {
      await api.post('/tenant/oauth/instagram/disconnect');
      toast.success('Instagram desconectado');
      await load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    } finally {
      setDisconnecting(false);
    }
  };

  const reconnect = async () => {
    setReconnecting(true);
    try {
      const ok = await disconnect({ confirm: false });
      if (!ok) return;
      await connect();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo reiniciar la conexión con Instagram');
      setReconnecting(false);
    }
  };

  const toggleAi = async (checked) => {
    setTogglingAi(true);
    try {
      await api.post('/tenant/oauth/instagram/ai-toggle', { enabled: checked });
      setAiEnabled(checked);
      toast.success(checked ? 'La IA va a responder Instagram automáticamente' : 'La IA ya no responde Instagram automáticamente');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTogglingAi(false); }
  };

  if (status === 'loading') {
    return <div className="flex items-center gap-2 text-sm text-[#64748B] py-2"><Loader2 className="w-4 h-4 animate-spin" /> Buscando tu cuenta de Instagram...</div>;
  }

  if (status === 'error') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <Button onClick={load} variant="outline" size="sm" className="gap-2"><RefreshCw className="w-4 h-4" /> Reintentar</Button>
      </div>
    );
  }

  if (status === 'not_connected') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        {incompleteAuth && (
          <div className="flex items-start gap-2 border border-red-200 bg-red-50 text-red-900 rounded-lg px-3 py-2 mb-3 text-xs" data-testid="instagram-incomplete-auth">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              <strong>Autorización de Instagram incompleta.</strong> No se guardó ninguna conexión. Verifica que
              la pantalla de Meta haya mostrado la cuenta de Instagram correcta antes de aceptar, y vuelve a intentarlo.
            </span>
          </div>
        )}
        <p className="text-sm text-[#64748B] mb-3">
          Conecta tu cuenta profesional de Instagram para que la IA responda mensajes directos y comentarios, y para publicar desde el calendario de Publicaciones.
        </p>
        <Button onClick={connect} disabled={redirecting} className="gap-2" data-testid="instagram-connect-button">
          {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          {incompleteAuth ? 'Reintentar conexión' : 'Conectar Instagram'}
        </Button>
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'} data-testid="instagram-connected">
      {(tokenExpired || reconnectRequired) && (
        <div className="flex flex-col gap-2 border border-red-200 bg-red-50 text-red-900 rounded-lg px-3 py-2 mb-3 text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              {tokenExpired
                ? 'El token de Instagram expiró. Pulsa Desconectar y vuelve a Conectar Instagram para generar uno nuevo (con el Instagram App Secret ya corregido en Infraestructura).'
                : 'Instagram necesita reconexión para activar mensajes y comentarios.'}
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs self-start"
            onClick={reconnect}
            disabled={disconnecting || reconnecting || redirecting}
            data-testid="instagram-reconnect-button"
          >
            {(disconnecting || reconnecting || redirecting) ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Desconectar y reconectar
          </Button>
        </div>
      )}
      {longLivedToken === false && !tokenExpired && (
        <div className="flex flex-col gap-2 border border-amber-200 bg-amber-50 text-amber-800 rounded-lg px-3 py-2 mb-3 text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Conectado con token de corta duración (expira en ~1 h). Pulsa el botón para volver a autorizar con el Instagram App Secret ya actualizado en Infraestructura &gt; Instagram Login (Business login settings — no el App Secret de Facebook).</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs self-start"
            onClick={reconnect}
            disabled={disconnecting || reconnecting || redirecting}
          >
            {(disconnecting || reconnecting || redirecting) ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Reconectar Instagram
          </Button>
        </div>
      )}
      {webhookSubscribed === false && (
        <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-lg px-3 py-2 mb-3 text-xs space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Meta no confirmó la suscripción a mensajes/comentarios. Si tu Instagram está ligado a una Fan Page de Facebook, conecta también <strong>Meta Fan Page</strong> (paso 2) y pulsa Reintentar aquí — usaremos la suscripción de la página. Si no, el Super Admin debe verificar en developers.facebook.com que Webhooks esté activo para el objeto Instagram (messages, comments) con callback https://app.cavernicolapp.com/api/webhooks/meta</span>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
            try {
              await api.post('/tenant/oauth/instagram/resubscribe-webhooks');
              toast.success('Suscripción a webhooks reintentada');
              await load();
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}>Reintentar suscripción</Button>
        </div>
      )}
      <div className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 mb-3 bg-[#F8FAFC]">
        <div>
          <p className="text-sm font-medium text-[#0F172A] flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5 text-emerald-600" /> {username ? `@${username}` : 'Conectado'}
          </p>
          <p className="text-[11px] text-[#94A3B8]">Mensajes directos, comentarios y publicaciones activos.</p>
        </div>
        <Button size="sm" variant="outline" onClick={disconnect} disabled={disconnecting} title="Desconectar">
          {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
        </Button>
      </div>
      <div className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2">
        <div>
          <p className="text-sm font-medium text-[#0F172A]">La IA responde Instagram automáticamente</p>
          <p className="text-[11px] text-[#94A3B8]">Apágalo si prefieres que tu equipo conteste manualmente por ahora.</p>
        </div>
        <Switch checked={aiEnabled} disabled={togglingAi} onCheckedChange={toggleAi} />
      </div>
    </div>
  );
}
